import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  CircularProgress,
  Box
} from '@mui/material';
import { ChangeTrackingService } from '../../../../services/changeTrackingService';
import type { PermissionFlags } from '../PersonPage.types';

interface CollaboratorsCardProps {
  personId: string;
  permissions: PermissionFlags;
}

interface Contributor {
  userId: string;
  userName: string;
  changeCount: number;
}

/**
 * CollaboratorsCard - Shows recent contributors
 *
 * Displays:
 * - Recent contributors with avatars (initials)
 * - Number of contributions
 * - Pending approvals badge (admins only)
 * - Empty state when no contributors
 */
export const CollaboratorsCard: React.FC<CollaboratorsCardProps> = ({
  personId,
  permissions
}) => {
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContributors = async () => {
      try {
        setLoading(true);

        // Fetch recent changes for this person
        const changes = await ChangeTrackingService.getRecentActivityForPerson(personId, 50);

        // Extract unique contributors and count their contributions
        const contributorMap = new Map<string, Contributor>();

        changes.forEach(change => {
          if (change.userId && change.userName) {
            const existing = contributorMap.get(change.userId);
            if (existing) {
              existing.changeCount++;
            } else {
              contributorMap.set(change.userId, {
                userId: change.userId,
                userName: change.userName,
                changeCount: 1
              });
            }
          }
        });

        // Convert to array and sort by contribution count (descending)
        const contributorsList = Array.from(contributorMap.values())
          .sort((a, b) => b.changeCount - a.changeCount)
          .slice(0, 5); // Show top 5 contributors

        setContributors(contributorsList);

        // If admin, get pending approvals count
        if (permissions.canApprove) {
          const pendingChanges = await ChangeTrackingService.getPendingChanges(100);
          const personPending = pendingChanges.filter(c => c.personId === personId);
          setPendingCount(personPending.length);
        }
      } catch (error) {
        console.error('Failed to load contributors:', error);
      } finally {
        setLoading(false);
      }
    };

    loadContributors();
  }, [personId, permissions.canApprove]);

  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Card>
      <CardHeader
        title="Contributors"
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        action={
          permissions.canApprove && pendingCount > 0 ? (
            <Chip
              label={`${pendingCount} pending`}
              color="warning"
              size="small"
            />
          ) : undefined
        }
      />
      <CardContent sx={{ pt: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : contributors.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No recent contributors
          </Typography>
        ) : (
          <List dense>
            {contributors.map(contributor => (
              <ListItem key={contributor.userId} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem',
                      bgcolor: 'primary.main'
                    }}
                  >
                    {getInitials(contributor.userName)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={contributor.userName}
                  secondary={`${contributor.changeCount} contribution${contributor.changeCount !== 1 ? 's' : ''}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};
